/**
 * Product gallery — Swiper.js (synced main + thumbnails)
 */
(function () {
  'use strict';

  const DESKTOP_MQ = '(min-width: 769px)';

  class ProductGallerySwiper {
    constructor($scope) {
      this.$scope = $scope;
      this.$root = $scope.find('[data-swiper-gallery]').first();
      if (!this.$root.length) return;

      this.sectionId = this.$root.data('section-id');
      this.$mainEl = this.$root.find('.product-gallery-swiper__main')[0];
      this.$thumbsEl = this.$root.find('.product-gallery-swiper__thumbs')[0];
      this.thumbsVisible = parseInt(this.$root.data('thumbs-visible'), 10) || 5;
      this._variantGroup =
        $scope.find('.enable_variant_image_group').length > 0 ||
        $scope.data('variant-image-group') === true ||
        $scope.data('variant-image-group') === 'true';
      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this._activeFilter = null;
      this._resizeTimer = null;
      this._onResize = null;

      this.init();
    }

    isDesktop() {
      return window.matchMedia(DESKTOP_MQ).matches;
    }

    setViewportHeight() {
      const header =
        document.querySelector('.section-header') ||
        document.querySelector('sticky-header') ||
        document.querySelector('header');
      const headerH = header ? header.getBoundingClientRect().height : 100;
      this.$root[0].style.setProperty('--pg-sticky-top', `${Math.round(headerH + 12)}px`);
    }

    bindAutoHeight() {
      if (!this.mainSwiper) return;

      const refresh = () => {
        this.mainSwiper.updateAutoHeight(300);
        this.mainSwiper.update();
      };

      this.mainSwiper.on('slideChangeTransitionEnd', refresh);
      this.mainSwiper.on('imagesReady', refresh);

      this.$root.find('.product-gallery-swiper__img').each(function () {
        if (this.complete) return;
        this.addEventListener('load', refresh, { once: true });
      });
    }

    getThumbsOptions() {
      return {
        direction: this.isDesktop() ? 'vertical' : 'horizontal',
        slidesPerView: 'auto',
        spaceBetween: 8,
        freeMode: false,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        mousewheel: this.isDesktop()
          ? { forceToAxis: true, releaseOnEdges: true, sensitivity: 0.8 }
          : false,
        keyboard: { enabled: true, onlyInViewport: true },
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous thumbnail',
          nextSlideMessage: 'Next thumbnail',
        },
        breakpoints: {
          0: {
            direction: 'horizontal',
            slidesPerView: 'auto',
            spaceBetween: 8,
            freeMode: false,
          },
          769: {
            direction: 'vertical',
            slidesPerView: 'auto',
            spaceBetween: 8,
            freeMode: false,
          },
        },
      };
    }

    isSlideVisible(slide) {
      return slide && !slide.classList.contains('swiper-slide--hidden');
    }

    getVisibleSlideIndices(swiper) {
      if (!swiper?.slides) return [];
      const indices = [];
      for (let i = 0; i < swiper.slides.length; i++) {
        if (this.isSlideVisible(swiper.slides[i])) indices.push(i);
      }
      return indices;
    }

    getNextVisibleIndex(swiper, fromIndex, direction) {
      const visible = this.getVisibleSlideIndices(swiper);
      if (!visible.length) return fromIndex;
      const pos = visible.indexOf(fromIndex);
      if (pos === -1) return visible[0];
      const nextPos = pos + direction;
      if (nextPos < 0 || nextPos >= visible.length) return fromIndex;
      return visible[nextPos];
    }

    slideMainTo(index, speed) {
      if (!this.mainSwiper || index < 0) return;
      this.mainSwiper.slideTo(index, speed ?? 350);
      this.syncThumbsToMain();
    }

    slideMainBy(direction) {
      if (!this.mainSwiper) return;
      const nextIndex = this.getNextVisibleIndex(
        this.mainSwiper,
        this.mainSwiper.activeIndex,
        direction
      );
      if (nextIndex !== this.mainSwiper.activeIndex) {
        this.slideMainTo(nextIndex);
      }
    }

    syncThumbsToMain() {
      if (!this.mainSwiper || !this.thumbsSwiper) return;
      const index = this.mainSwiper.activeIndex;
      this.thumbsSwiper.slideTo(index, 300);
      this.updateThumbArrows();
    }

    updateThumbArrows() {
      if (!this.mainSwiper) return;
      const visible = this.getVisibleSlideIndices(this.mainSwiper);
      const active = this.mainSwiper.activeIndex;
      const pos = visible.indexOf(active);
      const prev = this.$root.find('.product-gallery-swiper__thumbs-btn--prev');
      const next = this.$root.find('.product-gallery-swiper__thumbs-btn--next');
      prev.toggleClass('swiper-button-disabled', pos <= 0);
      next.toggleClass('swiper-button-disabled', pos < 0 || pos >= visible.length - 1);
    }

    bindThumbArrows() {
      const self = this;
      this.$root
        .find('.product-gallery-swiper__thumbs-btn--prev')
        .off('click.pgSwiper')
        .on('click.pgSwiper', (e) => {
          e.preventDefault();
          self.slideMainBy(-1);
        });
      this.$root
        .find('.product-gallery-swiper__thumbs-btn--next')
        .off('click.pgSwiper')
        .on('click.pgSwiper', (e) => {
          e.preventDefault();
          self.slideMainBy(1);
        });
    }

    bindThumbClicks() {
      const self = this;
      this.$root
        .find('.product-gallery-swiper__thumbs')
        .off('click.pgSwiper', '.product-gallery-swiper__thumb-btn')
        .on('click.pgSwiper', '.product-gallery-swiper__thumb-btn', function (e) {
          e.preventDefault();
          const slide = this.closest('.swiper-slide');
          if (!slide || !self.thumbsSwiper) return;
          const index = Array.prototype.indexOf.call(self.thumbsSwiper.slides, slide);
          if (index >= 0) self.slideMainTo(index);
        });
    }

    bindMainThumbSync() {
      if (!this.mainSwiper) return;
      this.mainSwiper.on('slideChange', () => {
        this.syncThumbsToMain();
        this.mainSwiper.updateAutoHeight(300);
      });
      this.thumbsSwiper?.on('click', () => {
        window.setTimeout(() => this.syncThumbsToMain(), 0);
      });
    }

    getMainOptions(thumbsSwiper) {
      const paginationEl = this.$root.find('.product-gallery-swiper__pagination')[0];
      const shared = {
        spaceBetween: 0,
        speed: 350,
        allowTouchMove: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        keyboard: { enabled: true, onlyInViewport: true },
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous product image',
          nextSlideMessage: 'Next product image',
        },
        thumbs: {
          swiper: thumbsSwiper,
          autoScrollOffset: 1,
        },
      };

      if (this.isDesktop()) {
        return {
          ...shared,
          autoHeight: true,
          effect: 'fade',
          fadeEffect: { crossFade: true },
        };
      }

      return {
        ...shared,
        effect: 'slide',
        slidesPerView: 1,
        slidesPerGroup: 1,
        autoHeight: true,
        grabCursor: true,
        resistanceRatio: 0.85,
        longSwipesRatio: 0.25,
        shortSwipes: true,
        followFinger: true,
        touchStartPreventDefault: false,
        pagination: paginationEl
          ? {
              el: paginationEl,
              clickable: true,
              dynamicBullets: true,
              dynamicMainBullets: 5,
            }
          : false,
      };
    }

    createMainSwiper(initialSlide) {
      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }

      const options = this.getMainOptions(this.thumbsSwiper);
      options.initialSlide = initialSlide ?? 0;
      this.mainSwiper = new Swiper(this.$mainEl, options);

      this.$root.toggleClass(
        'product-gallery-swiper--mobile-slide',
        !this.isDesktop()
      );

      this.bindMediaLifecycle();
      this.bindAutoHeight();
      this.bindMainThumbSync();
    }

    pauseActiveMedia() {
      const active = this.mainSwiper?.slides?.[this.mainSwiper.activeIndex];
      if (!active) return;
      active.querySelectorAll('video').forEach((v) => {
        try { v.pause(); } catch (e) { /* ignore */ }
      });
    }

    playActiveMedia() {
      const active = this.mainSwiper?.slides?.[this.mainSwiper.activeIndex];
      if (!active) return;
      const video = active.querySelector('video');
      if (video) {
        try { video.play(); } catch (e) { /* ignore */ }
      }
    }

    bindMediaLifecycle() {
      if (!this.mainSwiper) return;
      this.mainSwiper.on('slideChangeTransitionStart', () => this.pauseActiveMedia());
      this.mainSwiper.on('slideChangeTransitionEnd', () => this.playActiveMedia());
    }

    init() {
      if (typeof Swiper === 'undefined' || !this.$mainEl || !this.$thumbsEl) return;

      this.setViewportHeight();

      this.thumbsSwiper = new Swiper(this.$thumbsEl, this.getThumbsOptions());
      this._lastViewportDesktop = this.isDesktop();
      this.createMainSwiper(0);

      this.bindResize();
      this.bindVariantFilters();
      this.bindThumbArrows();
      this.bindThumbClicks();

      this.$scope.addClass('product-gallery-swiper--ready');
      this.$root.attr('data-gallery-ready', 'true');

      window.setTimeout(() => {
        this.updateAll();
        this.syncThumbsToMain();
        this.mainSwiper?.updateAutoHeight(0);
        if (this._variantGroup) {
          this.syncFromVariantPicker();
        }
      }, 100);

      window.setTimeout(() => this.updateAll(), 300);
    }

    updateAll() {
      this.setViewportHeight();
      this.thumbsSwiper?.update();
      this.mainSwiper?.update();
      this.mainSwiper?.updateSize();
      this.thumbsSwiper?.updateSize();
    }

    bindResize() {
      this._onResize = () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = window.setTimeout(() => {
          if (!this.thumbsSwiper) return;

          const isDesktop = this.isDesktop();
          if (this._lastViewportDesktop !== isDesktop) {
            const idx = this.mainSwiper?.activeIndex ?? 0;
            this.createMainSwiper(idx);
            this._lastViewportDesktop = isDesktop;
          }

          const dir = isDesktop ? 'vertical' : 'horizontal';
          if (this.thumbsSwiper.params.direction !== dir) {
            this.thumbsSwiper.changeDirection(dir);
          }
          this.updateAll();
          this.syncThumbsToMain();
        }, 150);
      };
      window.addEventListener('resize', this._onResize);
    }

    destroy() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      this.$root.find('.product-gallery-swiper__thumbs-btn').off('click.pgSwiper');
      this.$root.find('.product-gallery-swiper__thumbs').off('click.pgSwiper');
      this.mainSwiper?.destroy(true, true);
      this.thumbsSwiper?.destroy(true, true);
      this.$scope.removeClass('product-gallery-swiper--ready');
    }

    slideMatches($el, selector) {
      if (!selector) return true;
      const parts = selector.split(',').map((s) => s.trim()).filter(Boolean);
      if (parts.length > 1) return parts.some((part) => $el.is(part));
      return $el.is(selector);
    }

    setSlideVisibility() {
      const selector = this._activeFilter || null;
      $(this.$mainEl).find('.swiper-slide').each((_, slide) => {
        const $slide = $(slide);
        $slide.toggleClass('swiper-slide--hidden', !this.slideMatches($slide, selector));
      });
      $(this.$thumbsEl).find('.swiper-slide').each((_, slide) => {
        const $slide = $(slide);
        $slide.toggleClass('swiper-slide--hidden', !this.slideMatches($slide, selector));
      });
      this.updateAll();
    }

    getFirstVisibleMainIndex() {
      if (!this.mainSwiper?.slides) return 0;
      for (let i = 0; i < this.mainSwiper.slides.length; i++) {
        if (!this.mainSwiper.slides[i].classList.contains('swiper-slide--hidden')) return i;
      }
      return 0;
    }

    hasAnyMatch(selector) {
      if (!selector) return true;
      let found = false;
      $(this.$mainEl).find('.swiper-slide').each((_, el) => {
        if (this.slideMatches($(el), selector)) { found = true; return false; }
      });
      return found;
    }

    getFeaturedMediaId(variant) {
      if (!variant) return null;
      return variant.featured_media?.id || variant.featured_image?.id || null;
    }

    shopifyHandle(str) {
      return String(str || '').toLowerCase().trim()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }

    buildFilterFromVariant(variant) {
      if (!variant?.options?.length) return null;
      for (let i = 0; i < variant.options.length; i++) {
        const selector = `.filter-${this.shopifyHandle(variant.options[i])}`;
        if (this.hasAnyMatch(selector)) return selector;
      }
      return null;
    }

    goToFeaturedMedia(featuredMediaId) {
      if (!featuredMediaId || !this.mainSwiper?.slides) return false;
      let mainIndex = -1;
      let thumbIndex = -1;

      for (let i = 0; i < this.mainSwiper.slides.length; i++) {
        const slide = this.mainSwiper.slides[i];
        if (slide.classList.contains('swiper-slide--hidden')) continue;
        const box = slide.querySelector('.product-gallery-swiper__media-box');
        if (
          slide.querySelector(`[data-media-id="${featuredMediaId}"]`) ||
          (box && String(box.getAttribute('data-media-id')) === String(featuredMediaId))
        ) {
          mainIndex = i;
          break;
        }
      }

      if (this.thumbsSwiper?.slides) {
        const sel = `[data-media-id$="-${featuredMediaId}"]`;
        for (let t = 0; t < this.thumbsSwiper.slides.length; t++) {
          const slide = this.thumbsSwiper.slides[t];
          if (slide.classList.contains('swiper-slide--hidden')) continue;
          if (slide.matches(sel) || slide.querySelector(sel)) { thumbIndex = t; break; }
        }
      }

      if (mainIndex >= 0) this.slideMainTo(mainIndex, 300);
      else if (thumbIndex >= 0 && this.thumbsSwiper) {
        this.thumbsSwiper.slideTo(thumbIndex, 300);
        this.syncThumbsToMain();
      }
      return mainIndex >= 0 || thumbIndex >= 0;
    }

    applyFilter(selector, variant) {
      if (!this._variantGroup) {
        if (variant) this.goToFeaturedMedia(this.getFeaturedMediaId(variant));
        return;
      }
      if (selector && !this.hasAnyMatch(selector)) selector = null;
      this._activeFilter = selector || null;
      this.setSlideVisibility();
      const mediaId = variant ? this.getFeaturedMediaId(variant) : null;
      if (mediaId && this.goToFeaturedMedia(mediaId)) return;
      const index = this.getFirstVisibleMainIndex();
      if (this.mainSwiper && index >= 0) {
        this.slideMainTo(index, 0);
      }
    }

    getSelectedVariant($picker) {
      const jsonText = $picker.find('[type="application/json"]').first().text();
      if (!jsonText) return null;
      let variants;
      try { variants = JSON.parse(jsonText); } catch (e) { return null; }
      const options = [];
      $picker.find('fieldset').each(function () {
        const $checked = $(this).find('input:checked');
        if ($checked.length) options.push($checked.val());
      });
      if (!options.length) return null;
      return variants.find((v) =>
        !v.options.map((o, i) => options[i] === o).includes(false)
      );
    }

    syncFromVariant(variant) {
      if (!variant || !this.mainSwiper) return;
      const $checkedFilter = this.$scope.find('[data-filter]:checked');
      if ($checkedFilter.length && $checkedFilter.data('filter') && this._variantGroup) {
        this.applyFilter($checkedFilter.data('filter'), variant);
        return;
      }
      if (this._variantGroup) {
        this.applyFilter(this.buildFilterFromVariant(variant), variant);
        return;
      }
      this.goToFeaturedMedia(this.getFeaturedMediaId(variant));
    }

    scrollToGalleryMobile() {
      if (this.isDesktop()) return;
      const galleryCol =
        this.$root.closest('.plp__gallery-col')[0] ||
        this.$root[0];
      const header =
        document.querySelector('.section-header-navigation') ||
        document.querySelector('sticky-header') ||
        document.querySelector('.section-header');
      const announcement = document.querySelector('.announcement-bar');
      const headerH = header ? header.getBoundingClientRect().height : 0;
      const annH = announcement ? announcement.getBoundingClientRect().height : 0;
      const offset = headerH + annH + 8;
      const top = galleryCol.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    onVariantPickerChange() {
      window.setTimeout(() => {
        this.syncFromVariantPicker();
        this.scrollToGalleryMobile();
      }, 80);
    }

    syncVariantLabelsAndForm($picker) {
      const variant = this.getSelectedVariant($picker);
      if (!variant) return;

      $picker[0].querySelectorAll('.product-form__input').forEach((option) => {
        const header = option.querySelector('[data-header-option]');
        const radio = option.querySelector('input:checked');
        const select = option.querySelector('select');
        const value = radio ? radio.value : select ? select.value : null;
        if (value && header) header.textContent = value;
      });

      const productId = $picker.data('product');
      if (!productId) return;

      document
        .querySelectorAll(`#product-form-${productId}, #product-form-installment-${productId}`)
        .forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (input && String(input.value) !== String(variant.id)) {
            input.value = variant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
    }

    syncDefaultVariantPrice($picker) {
      const variant = this.getSelectedVariant($picker);
      if (!variant) return;

      const productId = $picker.data('product');
      const sectionId = $picker.data('section');
      const url = $picker.data('url');
      if (!productId || !sectionId || !url) return;

      fetch(`${url}?variant=${variant.id}&section_id=${sectionId}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const destination = document.getElementById(`product-price-${productId}`);
          const source = html.getElementById(`product-price-${productId}`);
          if (source && destination) destination.innerHTML = source.innerHTML;
        })
        .catch(() => {});

      if (!document.querySelector('.featured-product')) {
        window.history.replaceState({}, '', `${url}?variant=${variant.id}`);
      }
    }

    syncFromVariantPicker() {
      const $picker = this.$scope.find('variant-radios, variant-selects').first();
      if (!$picker.length) return;

      this.syncVariantLabelsAndForm($picker);

      const pickerEl = $picker[0];
      const checkedInput =
        $picker.find('fieldset input:checked')[0] || $picker.find('select')[0];
      const VariantRadios = customElements.get('variant-radios');
      const VariantSelects = customElements.get('variant-selects');

      try {
        if (checkedInput && VariantRadios && pickerEl instanceof VariantRadios) {
          pickerEl.onVariantChange({ target: checkedInput });
        } else if (checkedInput && VariantSelects && pickerEl instanceof VariantSelects) {
          pickerEl.onVariantChange({ target: checkedInput });
        } else {
          this.syncDefaultVariantPrice($picker);
        }
      } catch (e) {
        this.syncDefaultVariantPrice($picker);
      }

      const variant = this.getSelectedVariant($picker);
      if (variant) this.syncFromVariant(variant);
    }

    bindVariantFilters() {
      const self = this;
      const $picker = this.$scope.find('variant-radios, variant-selects');
      $picker
        .off('change.productGallerySwiper', 'input.product-form__radio, select')
        .on('change.productGallerySwiper', 'input.product-form__radio, select', () => {
          self.onVariantPickerChange();
        });
    }
  }

  window.ProductGallerySwiper = {
    init($scope) {
      if (!$scope?.length || typeof Swiper === 'undefined') return null;
      const existing = $scope.data('productGallerySwiper');
      if (existing?.mainSwiper) return existing;
      const instance = new ProductGallerySwiper($scope);
      if (!instance.mainSwiper) return null;
      $scope.data('productGallerySwiper', instance);
      return instance;
    },
  };

  function bootFromScope($scope) {
    return window.ProductGallerySwiper.init($scope);
  }

  window.initProductGallerySwiperForScope = bootFromScope;

  function bootAll() {
    if (typeof Swiper === 'undefined' || typeof jQuery === 'undefined') {
      window.setTimeout(bootAll, 50);
      return;
    }
    $('.productView').each(function () {
      const $el = $(this);
      if ($el.find('[data-swiper-gallery]').length && !$el.data('productGallerySwiper')) {
        bootFromScope($el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const $scope = $(event.target).find('.productView');
    if ($scope.length && $scope.find('[data-swiper-gallery]').length) {
      bootFromScope($scope.first());
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const $scope = $(event.target).find('.productView');
    const instance = $scope.data('productGallerySwiper');
    if (instance?.destroy) instance.destroy();
  });
})();
